// Confirmation dialog for destructive/irreversible actions, on Radix AlertDialog:
// focus is trapped, Cancel receives initial focus, focus returns to the element
// that opened it, and the portal makes it safe to render inside a table cell.
// Escape and a backdrop click cancel — both disabled while `busy`.

import { useRef, type ReactNode } from "react";
import { Button } from "@/ui";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "@/components/ui/dialog";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  children?: ReactNode;
  confirmLabel: string;
  /** Style the confirm button as destructive. */
  danger?: boolean;
  /** Disables both buttons (and Escape/backdrop cancel) while the action runs. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  // Radix only restores focus to a <Trigger>; these dialogs are opened by
  // ordinary buttons, so remember whatever had focus when it opened.
  const returnFocus = useRef<HTMLElement | null>(null);

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onCancel();
      }}
    >
      <AlertDialogContent
        onOverlayClick={busy ? undefined : onCancel}
        onEscapeKeyDown={(e) => {
          if (busy) e.preventDefault();
        }}
        onOpenAutoFocus={() => {
          returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          returnFocus.current?.focus();
          returnFocus.current = null;
        }}
        {...(children ? {} : { "aria-describedby": undefined })}
      >
        <AlertDialogTitle>{title}</AlertDialogTitle>
        {children && (
          <AlertDialogDescription asChild>
            <div className="flex flex-col gap-3">{children}</div>
          </AlertDialogDescription>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Batal</AlertDialogCancel>
          <Button variant={danger ? "danger" : "secondary"} onClick={onConfirm} disabled={busy}>
            {busy ? "Memproses…" : confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
